import { useCallback, useEffect, useState } from 'react';
import { AdAccount, getBusinessWallet } from '../api/ads/ad-account.api';
import { getUserBalanceBuckets } from '../api/payment/store.api';
import { useFeatureFlags } from './useFeatureFlags';
import { isIndividualAccount, normalizeRate, OrderFunding } from '../utils/individual-order';

/**
 * Nguồn tiền của tài khoản đang đặt đơn: doanh nghiệp ⇒ số dư ví VND; cá nhân ⇒ Ca nạp + tỉ giá hiện tại
 * (topup.vndPerCa). Với chiến dịch đã có thì dùng tỉ giá đã chụp trên chiến dịch (campaignVndPerCa, nếu là số dương). Đơn cá nhân bị giữ theo tỉ giá chụp lúc tạo đơn ở backend nên số hiển thị là ước tính.
 */
export const useOrderFunding = (
  account: AdAccount | null | undefined,
  campaignVndPerCa?: number | null
): { funding: OrderFunding; reload: () => void } => {
  const { get } = useFeatureFlags();
  const individual = !!account && isIndividualAccount(account);
  // Số dư gắn với id tài khoản đã tải; đổi tài khoản thì số cũ không còn hiệu lực (coi như chưa biết).
  const [loaded, setLoaded] = useState<{ id: string; balanceVnd: number | null; topUpCa: number | null } | null>(null);
  const current = account && loaded?.id === account.id ? loaded : null;
  const balanceVnd = current?.balanceVnd ?? null;
  const topUpCa = current?.topUpCa ?? null;

  const reload = useCallback(() => {
    if (!account) return;
    if (individual) {
      getUserBalanceBuckets()
        .then((b) => setLoaded({ id: account.id, balanceVnd: null, topUpCa: b.topUpBalance }))
        .catch(() => setLoaded(null));
    } else {
      getBusinessWallet(account.id)
        .then((w) => setLoaded({ id: account.id, balanceVnd: w.balanceVnd, topUpCa: null }))
        .catch(() => setLoaded(null));
    }
  }, [account, individual]);

  useEffect(() => {
    reload();
  }, [reload]);

  const funding: OrderFunding = individual
    ? {
        kind: 'Individual',
        topUpCa,
        vndPerCa: normalizeRate(
          typeof campaignVndPerCa === 'number' && campaignVndPerCa > 0 ? campaignVndPerCa : get('topup.vndPerCa')
        ),
      }
    : { kind: 'Business', balanceVnd };
  return { funding, reload };
};
